/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import {
  SEO,
  ButtonLink,
  ZapIcon,
  ShieldCheckIcon,
  SmileIcon,
  MapPinIcon,
  SmartphoneIcon,
  CreditCardIcon,
} from '../components/common';
import { colors, borderRadius, shadows, typography } from '../styles/theme';

const pageStyles = css`
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem 1rem;

  @media (min-width: 768px) {
    padding: 3rem 2rem;
  }
`;

const heroStyles = css`
  text-align: center;
  margin-bottom: 4rem;

  h1 {
    font-family: ${typography.fontFamily.heading};
    font-size: ${typography.fontSize['3xl']};
    color: ${colors.text.primary};
    margin-bottom: 1rem;

    @media (min-width: 768px) {
      font-size: ${typography.fontSize['4xl']};
    }
  }

  
  p {
    color: ${colors.text.secondary};
    font-size: ${typography.fontSize.lg};
    max-width: 700px;
    margin: 0 auto;
    line-height: 1.7;
  }
`;

const sectionStyles = css`
  margin-bottom: 4rem;

  h2 {
    font-family: ${typography.fontFamily.heading};
    font-size: ${typography.fontSize['2xl']};
    color: ${colors.text.primary};
    margin-bottom: 1.5rem;
    text-align: center;
  }
`;

const storyStyles = css`
  background: ${colors.background.primary};
  border-radius: ${borderRadius.xl};
  padding: 2rem;
  box-shadow: ${shadows.sm};

  @media (min-width: 768px) {
    padding: 3rem;
  }

  p {
    color: ${colors.text.secondary};
    font-size: ${typography.fontSize.lg};
    line-height: 1.8;
    margin-bottom: 1.5rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const valuesGridStyles = css`
  display: grid;
  gap: 1.5rem;
  grid-template-columns: 1fr;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const valueCardStyles = css`
  background: ${colors.background.primary};
  border-radius: ${borderRadius.xl};
  padding: 2rem;
  text-align: center;
  box-shadow: ${shadows.sm};
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${shadows.lg};
  }

  .icon {
    width: 56px;
    height: 56px;
    margin: 0 auto 1.25rem;
    border-radius: ${borderRadius.full};
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 26px;
      height: 26px;
    }
  }

  h3 {
    font-family: ${typography.fontFamily.heading};
    font-size: ${typography.fontSize.xl};
    color: ${colors.text.primary};
    margin-bottom: 0.75rem;
  }

  p {
    color: ${colors.text.secondary};
    font-size: ${typography.fontSize.sm};
    line-height: 1.6;
    margin: 0;
  }
`;

const ctaStyles = css`
  background: linear-gradient(135deg, ${colors.primary[600]} 0%, ${colors.primary[800]} 100%);
  border-radius: ${borderRadius['2xl']};
  padding: 3rem 2rem;
  text-align: center;
  color: white;

  @media (min-width: 768px) {
    padding: 4rem 3rem;
  }

  h2 {
    font-size: ${typography.fontSize['2xl']};
    margin-bottom: 1rem;
    color: white;
    text-align: center;

    @media (min-width: 768px) {
      font-size: ${typography.fontSize['3xl']};
    }
  }

  p {
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 2rem;
    max-width: 500px;
    margin-left: auto;
    margin-right: auto;
  }
`;


export default function AboutPage() {
  const values = [
    {
      icon: <ZapIcon />,
      color: colors.primary[100],
      iconColor: colors.primary[600],
      title: 'Hızlı Yanıt',
      description: 'Yolda kaldığınızda dakikalar içinde yanınızdayız.',
    },
    {
      icon: <ShieldCheckIcon />,
      color: '#dcfce7',
      iconColor: '#16a34a',
      title: 'Güvenilirlik',
      description: 'Profesyonel ekibimiz ile güvenli hizmet garantisi.',
    },
    {
      icon: <SmileIcon />,
      color: colors.secondary[100],
      iconColor: colors.secondary[600],
      title: 'Müşteri Memnuniyeti',
      description: '%99 müşteri memnuniyeti oranıyla hizmet veriyoruz.',
    },
    {
      icon: <MapPinIcon />,
      color: '#fef3c7',
      iconColor: '#d97706',
      title: '81 İl Kapsama',
      description: 'Türkiye\'nin her yerinde hizmet ağımız mevcut.',
    },
    {
      icon: <SmartphoneIcon />,
      color: '#ede9fe',
      iconColor: '#7c3aed',
      title: 'Kolay Erişim',
      description: 'Tek tık ile yardım çağırın, süreci takip edin.',
    },
    {
      icon: <CreditCardIcon />,
      color: '#cffafe',
      iconColor: '#0891b2',
      title: 'Şeffaf Fiyatlandırma',
      description: 'Gizli ücret yok, fiyatı önceden bilin.',
    },
  ];

  return (
    <>
      <SEO
        title="Hakkımızda"
        description="YolSepetiGO hakkında bilgi edinin. Çekici, yol yardım, nakliye ve vinç hizmetleri sunan güvenilir çözüm ortağınız."
        url="/hakkimizda"
      />

      <div css={pageStyles}>
        <header css={heroStyles}>
          <h1>Hakkımızda</h1>
          <p>
            YolSepetiGO olarak, yolda kaldığınızda yanınızda olan güvenilir
            çözüm ortağınız olmaktan gurur duyuyoruz. 7/24 kesintisiz hizmet
            anlayışımızla Türkiye'nin 81 ilinde sizlere ulaşıyoruz.
          </p>
        </header>

        <section css={sectionStyles}>
          <h2>Hikayemiz</h2>
          <div css={storyStyles}>
            <p>
              YolSepetiGO, 2020 yılında yolda kalan sürücülerin yaşadığı
              zorlukları çözmek amacıyla kuruldu. Geleneksel çekici ve yol
              yardım hizmetlerinin karmaşık ve güvensiz yapısını, teknoloji
              ile birleştirerek modern ve şeffaf bir platform oluşturduk.
            </p>
            <p>
              Bugün, binlerce sürücüye hizmet veren, yüzlerce hizmet sağlayıcı
              ile çalışan ve sürekli büyüyen bir ekosistem haline geldik.
              Amacımız, yolda kaldığınızda stresinizi minimuma indirmek ve
              size en hızlı, en güvenilir yardımı ulaştırmak.
            </p>
            <p>
              Bu blog ile de sizlere araç bakımı, yol güvenliği, çekici
              hizmetleri ve daha birçok konuda faydalı bilgiler sunmayı
              hedefliyoruz.
            </p>
          </div>
        </section>

        <section css={sectionStyles}>
          <h2>Değerlerimiz</h2>
          <div css={valuesGridStyles}>
            {values.map((value, index) => (
              <div key={index} css={valueCardStyles}>
                <div className="icon" style={{ background: value.color, color: value.iconColor }}>
                  {value.icon}
                </div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section css={ctaStyles}>
          <h2>Yolda mı Kaldınız?</h2>
          <p>
            YolSepetiGO ile hızlı ve güvenilir çekici hizmeti alın.
            7/24 yanınızdayız!
          </p>
          <ButtonLink href="https://yolsepetigo.com" variant="white" size="lg">
            Hemen Yardım Çağır
          </ButtonLink>
        </section>
      </div>
    </>
  );
}
